import JSONModel from "sap/ui/model/json/JSONModel";
import XMLView from "sap/ui/core/mvc/XMLView";
import AvatarColor from "sap/m/AvatarColor";
import ResponsivePopover from "sap/m/ResponsivePopover";
import Control from "sap/ui/core/Control";
import { Button$PressEvent } from "sap/m/Button";
import MessageHandler from "vamos/cockpit/main/app/handler/MessageHandler";
import ApiService from "vamos/cockpit/base/services/type/ApiService";
import { ReleaseDto, ResponseString } from "vamos/cockpit/model/api/vamos.private.api.types";
import BaseTypeController from "vamos/cockpit/base/controller/BaseTypeController";
import { Message, MessageKey } from "vamos/cockpit/model/odata/VamosModel";
import ViewHandler from "vamos/cockpit/base/handler/ViewHandler";
import MessageDialogController from "vamos/cockpit/main/app/controller/MessageDialog.controller";
import SupplierMessageData from "vamos/cockpit/main/app/message/model/SupplierMessageData";
import FilterOperator from "sap/ui/model/FilterOperator";
import Filter from "sap/ui/model/Filter";
import { RequestData } from "vamos/cockpit/main/app/message/model/RequestData";
import V4Context from "sap/ui/model/odata/v4/Context";
import ODataListBinding from "sap/ui/model/odata/v4/ODataListBinding";
import Sorter from "sap/ui/model/Sorter";

interface ReleaseTextDto {
    releaseGuid?: string;
    text?: string;
}

interface ResponseReleaseTextDtoList {
    additionalInfo?: Record<string, object>[];
    message?: string;
    content?: ReleaseTextDto[];
    requestId?: string;
}

export default class MessagePopoverController extends BaseTypeController {

    private parentView: XMLView = null;
    private notificationList = null;
    private updateReadCallback : () => void;

    private messageContexts: V4Context[] = [];
    private releaseTextCache: Record<string, string> = {};

    public onInit() {
        super.onInit();
        this.notificationList = this.getView().byId("notifList");
        const viewModel = new JSONModel({
            "onlyUnread": true,
            "messages": [],
            "releaseTextCache": this.releaseTextCache,
        });
        this.getView().setModel(viewModel, "view");
        this.notificationList.setModel(viewModel);

    }
    private async loadMessages(): Promise<void> {
        const binding = this.getModel().bindList("/Messages", undefined, [
            new Sorter("CreatedOn", true),
        ]) as ODataListBinding;
        const contexts = await binding.requestContexts(0, Infinity);
        this.messageContexts = contexts;
        const messages = contexts.map((context) => context.getObject() as Message);
        await this.loadReleaseTexts(messages);
        this.getView().getModel("view").setProperty("/messages", messages);

    }

    private async loadReleaseTexts(messages: Message[]): Promise<void> {
        const releaseGuids = messages
            .filter((message) => message.MessageKey === MessageKey.RELEASE_NOTES.toString())
            .map((message) => (JSON.parse(message.ReplacementContent) as ReleaseDto).releaseGuid)
            .filter((releaseGuid, index, guids) => releaseGuid && guids.indexOf(releaseGuid) === index && !(releaseGuid in this.releaseTextCache));

        if (releaseGuids.length === 0) {
            return;
        }

        try {
            const response = await ApiService.getInstance().postRestJsonData<ResponseReleaseTextDtoList>("/release/texts", releaseGuids);
            (response.data.content ?? []).forEach((releaseText) => {
                this.releaseTextCache[releaseText.releaseGuid] = releaseText.text;
            });
            this.getView().getModel("view").setProperty("/releaseTextCache", this.releaseTextCache);
        } catch (e) {
            console.error(e);
        }
    }

    public onAfterRendering() {
        if (!this.parentView) {
            this.parentView = this.findParentControl("sap.ui.core.mvc.XMLView", this.getView()) as XMLView;
        }
    }

    public async openDialog(control: Control | HTMLElement,updateCallback: () => void) {
        const popover = this.getView().byId("notificationsPopover") as ResponsivePopover;
        if (popover.isOpen()) {
            popover.close();
            return;
        }
        this.updateReadCallback = updateCallback;
        try {
            await this.loadMessages();
            popover.openBy(control);

        } catch (e) {
            console.error(e);
        }
    }

    public formatAvatarIcon(type: string) {
        switch (type) {
            case "ORDER_WITHDRAWAL": {
                return "sap-icon://activity-items";
            }
            case "RELEASE_NOTE": {
                return "sap-icon://feed";
            }
            case "SUPPLIER_SERVER_CONNECTION_FAILURE": {
                return "sap-icon://factory";
            }
            default:
                return;
        }
    }

    public formatAvatarColor(messageType: string) {
        switch (messageType) {
            case "SUCCESS":
                return AvatarColor.Accent7;
            case "WARNING":
                return AvatarColor.Accent1;
            case "INFO":
                return AvatarColor.Accent6;
            case "ERROR":
                return AvatarColor.Accent2;
            default:
                return;
        }
    }

    public formatButtonText(type: string) {
        return MessageHandler.handleButtonText(type);
    }

    public getTitleText(type: string, replacementContent: string) {
        return MessageHandler.handleMessageTitle(type, replacementContent);
    }

    public formatMessage(messageKey: string, replacementContent: string) {
        return MessageHandler.handleMessageDescription(messageKey, replacementContent);
    }

    public async onPressMessageButton(event: Button$PressEvent) {
        try {
            const button = event.getSource();
            const messageObject = button.getBindingContext()?.getObject() as Message;
            const context = this.messageContexts.find((c) => (c.getObject() as Message).Guid === messageObject?.Guid);

            if (messageObject) {
                let path;
                switch (messageObject.Type) {
                    case "ORDER_WITHDRAWAL": {
                        const content = JSON.parse(messageObject.ReplacementContent) as RequestData;
                        path = this.getKeyPredicateFromEntitySet("Request", { Guid: content.guid });
                        this.navTo("requestDetails", { path: path, tab: "request" }, false);
                        break;
                    }
                    case "RELEASE_NOTE": {
                        const releaseData = JSON.parse(messageObject.ReplacementContent) as ReleaseDto;
                        const releaseText = this.releaseTextCache[releaseData.releaseGuid]
                            ?? (await ApiService.getInstance().getRestJsonData<ResponseString>(`/release/${releaseData.releaseGuid}/text`)).content;
                        const view = await ViewHandler.getView("vamos.cockpit.main.app.view.MessageDialog", this.getComponent());
                        const controller = view.getController() as MessageDialogController;
                        controller.openDialog(MessageHandler.handleMessageTitle(messageObject.Type, messageObject.ReplacementContent), releaseText);
                        break;
                    }
                    case "SUPPLIER_SERVER_CONNECTION_FAILURE": {
                        const content = JSON.parse(messageObject.ReplacementContent) as SupplierMessageData;
                        path = this.getKeyPredicateFromEntitySet("Supplier", { Guid: content.supplierGuid });
                        this.navTo("supplierDetails", { path: path, tab: "supplier" }, false);
                        break;
                    }
                    default:
                        console.error(`${messageObject.Type} is not a valid type`);
                        return;
                }
                context?.setProperty("Unread", false).catch((e) => {
                });
                try {
                    await this.submitBatch(context.getUpdateGroupId());
                    context
                        .getBinding()
                        .requestRefresh()
                        .then(() => {
                                this.updateReadCallback();
                        });
                } catch (e) {
                    console.error(e);
                }
            }
        } catch (e) {
            console.error(e);
        }
    }

    public async beforePopoverClose() {
        this.messageContexts.forEach((context) => {
            if (context.getProperty("Unread")) {
                context.setProperty("Unread", false).catch(() => {
                });
            }
        });
        try {
            await this.submitBatch();
            this.updateReadCallback();
        } catch (e) {
            console.error(e);
        }
    }

    public onToggleUnread(event) {
        const state = this.getView().getModel("view").getProperty("/onlyUnread");
        const list = this.getView().byId("notifList");
        const filter = [];
        if (state) {
            filter.push(new Filter("Unread", FilterOperator.EQ, true));
        }
        list.getBinding("items").filter(filter);
    }
    public formatButtonVisibility(replacementContent: string, messageKey: string): boolean {
        if (messageKey !== MessageKey.RELEASE_NOTES.toString()) {
            return true;
        }
        const releaseData = JSON.parse(replacementContent) as ReleaseDto;
        return !!this.releaseTextCache[releaseData.releaseGuid];
    }
}